import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformReplaySummary } from "@tdelivr/api-client/generated/event-platform/event-replay-control.js";

import { boundedNote } from "../board.ts";
import {
  CREATE_REFUSAL,
  EVENT_REPLAYS_SCOPE,
  filterLabel,
  replayRow,
  replaySpanNote,
  statesPresent,
} from "../event-replays.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `replay` in `16 WP15 Platform Machinery.dc.html`: every tenant's event replays.
 *
 * ```
 * replay:['Replay control','EVT-04','Replays run with a concurrency limit and per-partition checkpoints. Sensitive streams need two approvers.',
 *   [['Running','1','active','48,210 events'],['Awaiting approval','1','human','1 of 2'],
 *    ['Blocked projections','1','caution','delivery projection'],['Completed today','3','done','']],
 *   ['Replay','Stream','Progress'],
 *   [['RPL-0311','payments.settled.v2','partitions 0–11 · 38%','active','running'], …],
 *   'info','Operations consoles show "replay in progress" while a projection is blocked.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                    | source                                              |
 * | ---------------------------------------- | --------------------------------------------------- |
 * | the rows                                 | `EVT-04 GET /v1/platform/event-replays` (ADR 0060)  |
 * | `Replay` — `RPL-0311`                    | `replays.replayId`, a UUID. Q-196 on the width       |
 * | `Stream` — `payments.settled.v2`         | `replays.topic`                                     |
 * | `Progress` — `100%`                      | `replays.status`, exactly                            |
 * | `Progress` — `—`                         | the design's own cell for a job that has not started |
 * | `Progress` — `partitions 0–11`           | **nothing** — only `partitionCount` leaves. ADR 0060 §4 |
 * | `Progress` — `38%`                       | **nothing** — no count of events replayed exists. Q-201 |
 * | the `1 of 2 approvals` badge             | `replays.approvalCount` and `requiredApprovals`      |
 * | the `running` / `completed` badges        | `replays.status`                                     |
 * | `Running · 48,210 events`                | **nothing** — a plan ceiling, not a count. Q-201     |
 * | `Blocked projections · delivery projection` | **nothing** — SEA-02's tile, and SEA-02 publishes nothing. Q-203 |
 * | `Completed today · 3`                    | **nothing** — no completion instant. R-04 for the count |
 * | `Request replay`                         | **nothing** — the plan is `/internal/v1`. `CREATE_REFUSAL` |
 *
 * **This is the highest-privilege control plane on the platform and the board is drawn from a
 * projection rather than from the record.** EVT-04's `security_class` reads "restricted privileged
 * operations and protected payload references"; a replay pushes past events through the platform
 * again and every consumer downstream treats them as new. So ADR 0060 publishes thirteen fields, two
 * of them derived counts, and the stored job document is never selected out of PostgreSQL for this
 * operation at all — a projection applied in TypeScript would have put every tenant's payload
 * references in one process's memory. This component asks for the one listing and draws what it
 * answers.
 *
 * `../event-replays.ts` is where the progress grammar, the approval pair and the three absent cells
 * are argued. Nothing about any of them is decided here.
 */

const eventReplays = services.event_platform__event_replay_control;

export interface EventReplaysProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Replay control`, which is the design's own trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Replay control";
const SOURCE = "EVT-04";
const DESCRIPTION =
  "Replays run with a concurrency limit and per-partition checkpoints. Sensitive streams need two approvers.";
const COLUMNS = ["Replay", "Stream", "Progress"] as const;
const NOTE = 'Operations consoles show "replay in progress" while a projection is blocked.';
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's `FORMS.replay` primary button, word for word. */
const PRIMARY_LABEL = "Request replay";

/**
 * The most rows one request answers, which is EVT-04's own maximum.
 *
 * Asked for explicitly rather than left to the service's default of fifty. `boundedNote` says when
 * the answer was full, and on this board that sentence matters more than on its siblings: the four
 * tiles the design draws are counts of the whole platform, so a board holding a partial page under a
 * heading that spans every tenant is exactly the R-04 error. The page is ordered by
 * `(tenant_id, replay_id)` and both halves are immutable, so a full answer is the head of the list
 * rather than an arbitrary slice.
 */
const PER_FETCH = 200;

export function EventReplays({ transport, session, crumbs }: EventReplaysProps): ReactNode {
  const [rows, setRows] = useState<readonly PlatformReplaySummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal.
     *
     * The board renders the console's own forbidden state naming the scope, which is what an
     * operator can act on — they can ask their administrator for `event-replay:platform:read` by
     * name. A 403 from the service renders the same state, so this is not the only guard; it is the
     * one that makes the scope a literal in this application's source, which is what
     * `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3.
     *
     * This scope and no other. EVT-04's four command scopes are not alternatives and two of them are
     * dangerous: `event-replay:approve` is half of a two-person control over a privileged operation,
     * and `event-replay:start` begins a replay every downstream consumer treats as live traffic.
     * ADR 0060 §2.
     */
    if (!session.scopes.has(EVENT_REPLAYS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [EVENT_REPLAYS_SCOPE] });
      return;
    }

    try {
      const client = eventReplays.createEventReplayControlClient(transport);
      /*
       * `listPlatformEventReplays`, which is the only read of this service a browser can reach.
       *
       * The alternative is not a tenant-bounded listing — there is none — but
       * `getEventReplay`, a by-identifier read under `/internal/v1/tenants/{tenantId}/…` that
       * returns the whole job document: the payload reference, the incident prose, the requester and
       * every approver. ADR 0060 left all five internal operations byte-for-byte as they were and
       * added this one beside them, and the router answers 404 on their platform-shaped paths.
       *
       * `after` is deliberately not sent. The first page is the board; see `PER_FETCH`.
       */
      const page = await client.listPlatformEventReplays(String(PER_FETCH));
      setRows(page.replays);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [EVENT_REPLAYS_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((replay) => state === "" || filterLabel(replay) === state).map(replayRow);

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "replay jobs",
    "ordered by tenant and then by replay",
  );
  const span = replaySpanNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No replay has been planned on any tenant yet. A row appears here when an operator plans one, and it waits for its approvals before it runs."
      primary={{ label: PRIMARY_LABEL, refusal: CREATE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="event-replays"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
